import Reveal from "@/components/site/Reveal";
import PageHero from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import Seo, { ORG_SCHEMA, serviceSchema, breadcrumbSchema } from "@/components/site/Seo";

const JURISDICTIONS = [
  { t: "Mainland Companies", d: "Trade freely across the UAE with 100% foreign ownership in most activities — ideal for client-facing and locally-licensed businesses." },
  { t: "Financial Free Zones", d: "World-class financial centres with English common-law frameworks — suited to funds, fintech, asset managers and family offices." },
  { t: "Commercial Free Zones", d: "Specialist free zones for trade, commodities, logistics, technology and professional services." },
  { t: "Cost-Efficient Free Zones", d: "Streamlined free zones for consultants, agencies and growing SMEs — fast formation and competitive licence fees." },
  { t: "Holding Structures", d: "Personal and family holding companies for asset consolidation, succession and tax-efficient wealth structuring." },
  { t: "Offshore Companies", d: "International business companies for cross-border trade, IP holding and investment vehicles." },
];

export default function BusinessSetup() {
  return (
    <div className="bg-white">
      <Seo
        title="UAE Business Setup — Free Zones & Mainland"
        description="End-to-end UAE company formation across mainland, DIFC, ADGM and specialist free zones. Velor Advisory structures, files and onboards your business."
        keywords="UAE business setup, Dubai company formation, UAE free zone company, mainland LLC UAE, DIFC company setup, offshore company UAE, UAE company registration"
        focusKeyword="UAE business setup"
        path="/business-setup"
        schema={[
          ORG_SCHEMA,
          serviceSchema({
            name: "UAE Business Setup & Company Formation",
            description:
              "Company formation and licensing across UAE mainland, DIFC, ADGM and specialist free zones — structuring, filing, bank onboarding and ongoing compliance.",
            path: "/business-setup",
            serviceType: "Company formation and corporate services",
            offers: {
              "@type": "OfferCatalog",
              name: "Jurisdictions",
              itemListElement: JURISDICTIONS.map((j) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: j.t, description: j.d },
              })),
            },
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Business Setup", path: "/business-setup" },
          ]),
        ]}
      />
      <PageHero
        overline="Business Setup & Company Formation"
        title="Your UAE company, formed with care."
        subtitle="Across more than forty UAE free zones, mainland and financial centres — we structure, file and onboard your company end-to-end."
        image="https://images.unsplash.com/photo-1524230572899-a752b3835840?auto=format&fit=crop&w=1650&q=75"
        imageAlt="Contemporary UAE office tower — representing company formation across mainland and free zones"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Approach</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Right structure. <em className="italic">Right jurisdiction.</em></h2>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7 text-black/70 font-light text-lg leading-relaxed space-y-5">
            <p>Choosing where and how to incorporate is the single most important decision in your UAE journey. We start with your business model, tax position and ambitions — then map them to the optimal jurisdiction, licence and shareholding.</p>
            <p>Every Velor engagement includes corporate banking introductions, visa quotas for your team, and ongoing PRO services so you can focus on growth.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#fafafa] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Jurisdictions</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">Where we incorporate.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10">
            {JURISDICTIONS.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 80} className="border-r border-b border-black/10 p-8 md:p-10 bg-white">
                <div className="text-[11px] tracking-overline uppercase text-black/45">0{i+1}</div>
                <h3 className="font-serif text-3xl mt-4">{p.t}</h3>
                <p className="mt-3 text-black/60 font-light leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">What's included</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-2xl">End to end support for launching and operating your UAE company.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { t: "Licence & Activity", d: "Selection, application and issuance of your trade licence." },
              { t: "MoA & Structure", d: "Memorandum of Association, shareholding and authority." },
              { t: "Banking Intros", d: "Personal and corporate accounts with our partner banks." },
              { t: "Visas & PRO", d: "Investor visa, staff visas, Emirates IDs and ongoing PRO." },
            ].map((x) => (
              <Reveal key={x.t}>
                <h3 className="font-serif text-2xl">{x.t}</h3>
                <p className="mt-3 text-black/60 font-light">{x.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-white/45">Begin</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Request a structuring <em className="italic">proposal</em>.</h2>
            <p className="mt-6 text-white/65 font-light max-w-md">Tell us about your business and we'll return a written, all-inclusive proposal within 24 hours.</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <div className="bg-white text-black"><LeadForm testid="business-lead-form" source="business-setup" /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
